import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { requiredTrimmed } from '../../../utils/utils';
import { InputComponent } from '../../from-input/input.component';

@Component({
    selector: 'add-focus-area',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, InputComponent, FormsModule],
    templateUrl: './add-focus-area.component.html',
    styleUrls: ['./add-focus-area.component.scss']
})
export class AddFocusAreaComponent implements OnInit, OnDestroy {
    @Input() initialData: any = null;
    @Output() formSubmitted = new EventEmitter<any>();
    @Output() formValidation = new EventEmitter<boolean>();
    noteText: string = '';
    form!: FormGroup;
    private dialogConfirmListener?: (event: Event) => void;

    customErrorMessages = {
        focusAreaName: { required: 'Please enter a focus area name.' },
    };

    constructor(private fb: FormBuilder) { }

    ngOnInit(): void {
        this.initForm();
        this.setupDialogListener();
    }

    ngOnDestroy(): void {
        this.cleanupDialogListener();
    }

    private initForm(): void {
        this.form = this.fb.group({
            focusAreaName: [this.initialData ? this.initialData['focus area name'] ?? this.initialData.focusAreaName ?? '' : '', requiredTrimmed],

            description: [
                this.initialData?.description || '',
                [this.noWhitespaceOnlyValidator.bind(this)] // Add validator for description
            ],
        });

        this.formValidation.emit(this.form.valid);
        this.form.valueChanges.subscribe(() => {
            this.formValidation.emit(this.form.valid);
        });
    }

    /**
     * Custom validator to reject values that contain only whitespace
     */
    private noWhitespaceOnlyValidator(control: AbstractControl): { [key: string]: any } | null {
        // Allow empty string (since description is optional)
        if (control.value === null || control.value === undefined || control.value === '') {
            return null;
        }

        // Check if value contains only whitespace
        if (control.value.trim().length === 0) {
            return { whitespaceOnly: true };
        }
        return null;
    }

    private setupDialogListener(): void {
        this.dialogConfirmListener = () => this.handleDialogConfirm();
        document.addEventListener('dialogConfirm', this.dialogConfirmListener);
    }

    private cleanupDialogListener(): void {
        if (this.dialogConfirmListener) {
            document.removeEventListener('dialogConfirm', this.dialogConfirmListener);
        }
    }

    handleDialogConfirm(): void {
        if (this.form.valid) {
            const formValue = this.form.value;
            // Trim the description value before emitting if it exists
            if (formValue.description) {
                formValue.description = formValue.description.trim();
            }
            this.formSubmitted.emit(formValue);
        } else {
            this.markFormAsTouched();
        }
    }

    onSubmit(): void {
        this.handleDialogConfirm();
    }

    private markFormAsTouched(): void {
        this.form.markAllAsTouched();
    }

    isFormValid(): boolean {
        return this.form.valid;
    }

    onNoteChange() {
        // console.log('Note entered:', this.noteText);
    }

    autoResize(event: Event) {
        const textarea = event.target as HTMLTextAreaElement;
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
    }

    hasDescriptionError(): boolean {
        const descriptionControl = this.form.get('description');
        return descriptionControl ? (descriptionControl.invalid && descriptionControl.touched) : false;
    }
    getDescriptionError(): string {
        const descriptionControl = this.form.get('description');
        if (descriptionControl && descriptionControl.errors && descriptionControl.touched) {
            if (descriptionControl.errors['whitespaceOnly']) {
                return 'Please enter a valid description.';
            }
        }
        return '';
    }
}